import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Avatar, EmptyState, Menu, Pill } from '@orion/ui';
import type { ContactResponse } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { ListPagination } from '../../components/ListPagination.js';
import { useAuth } from '../../lib/auth.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useCompaniesQuery } from '../companies/api.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useContactCountsQuery, useContactsQuery } from './api.js';

type Tab = 'all' | 'mine' | 'customers' | 'recent';

function sevenDaysAgoIso(): string {
  return new Date(Date.now() - 7 * 86_400_000).toISOString();
}

/** docs/architecture screen "Contacts — list". design/orion-crm-design/
 * rendered-html/Contacts.html's 4th tab, "Decision makers", is dropped —
 * there's no seniority/role field on Contact to back it, and a jobTitle
 * keyword guess would be exactly the fake categorization this pass avoids
 * everywhere else. Its "Tag" filter is dropped too: contacts have real
 * tags now (added this pass), but there's no tag-list reference endpoint
 * yet to populate the dropdown with. */
export function ContactsListPage() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [ownerId, setOwnerId] = useState<string | undefined>();

  const { data: counts } = useContactCountsQuery();
  const { data: companies } = useCompaniesQuery({ pageSize: 50 });
  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);

  const { data, isLoading, isError } = useContactsQuery({
    page,
    pageSize,
    q: q || undefined,
    companyId,
    ownerId: tab === 'mine' ? undefined : ownerId,
    lifecycleStage: tab === 'customers' ? 'customer' : undefined,
    createdAfter: tab === 'recent' ? sevenDaysAgoIso() : undefined,
  });
  const exportUrl = `/api/v1/contacts/export${q ? `?q=${encodeURIComponent(q)}` : ''}`;

  const total = data?.meta?.total ?? 0;
  const contacts = data?.data ?? [];

  const setTabFilter = (t: Tab) => {
    setTab(t);
    setPage(1);
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Contacts' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-hidden px-7 py-6">
        <div className="flex h-10 shrink-0 items-baseline justify-between">
          <div className="flex items-baseline gap-3">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">Contacts</h1>
            <span className="text-sm tabular-nums text-text-muted">{counts?.all ?? total} people</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/contacts/new" className="flex h-9 items-center gap-2 rounded-control bg-primary px-3.5 text-sm font-semibold text-white hover:bg-primary-hover">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New contact
            </Link>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card">
          <div role="tablist" aria-label="Saved views" className="flex h-11 shrink-0 items-stretch gap-5 border-b border-border px-4">
            {(
              [
                ['all', 'All', counts?.all],
                ['mine', 'My contacts', counts?.mine],
                ['customers', 'Customers', counts?.customers],
                ['recent', 'Recently added', counts?.recentlyAdded],
              ] as const
            ).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTabFilter(key)}
                className={tab === key ? 'flex items-center gap-1.5 border-b-2 border-primary text-sm font-semibold text-ink' : 'flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-ink'}
              >
                {label}
                <span className={tab === key ? 'rounded-full bg-primary-soft px-1.5 text-xs tabular-nums text-primary-text' : 'text-xs tabular-nums text-text-muted'}>{count ?? '—'}</span>
              </button>
            ))}
            {hasPermission('crm.contacts.export') && (
              <a href={exportUrl} className="ml-auto flex items-center text-[13px] font-semibold text-primary-text hover:underline">
                Export CSV
              </a>
            )}
          </div>

          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
            <label className="flex h-8 w-70 items-center gap-2 rounded-control border border-border-strong bg-surface px-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <span className="sr-only">Search contacts</span>
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                type="search"
                placeholder="Search name, email, phone…"
                className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none"
              />
            </label>
            <span className="mx-1 h-5 w-px bg-border" />
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1 rounded-control px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                  Company: <span className="font-semibold text-ink">{companies?.data.find((c) => c.id === companyId)?.name ?? 'Any'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              }
              items={[{ label: 'Any company', onSelect: () => setCompanyId(undefined) }, ...(companies?.data ?? []).map((c) => ({ label: c.name, onSelect: () => setCompanyId(c.id) }))]}
              align="left"
            />
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1 rounded-control px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                  Owner: <span className="font-semibold text-ink">{ownerId ? memberLabels.get(ownerId) : 'Anyone'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              }
              items={[{ label: 'Anyone', onSelect: () => setOwnerId(undefined) }, ...(members ?? []).map((m) => ({ label: memberLabels.get(m.id) ?? m.displayName, onSelect: () => setOwnerId(m.id) }))]}
              align="left"
            />
          </div>

          {isError && (
            <div role="alert" className="m-4 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
              Something went wrong loading contacts. Please try again.
            </div>
          )}

          {!isLoading && contacts.length === 0 && (
            <div className="p-6">
              <EmptyState title="No contacts yet" description="Contacts are created directly or by converting a lead." />
            </div>
          )}

          {contacts.length > 0 && (
            <div role="table" aria-label="Contacts" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div
                role="row"
                className="grid h-9 shrink-0 items-center gap-2 border-b border-border bg-ground px-4 text-xs font-semibold text-text-muted"
                style={{ gridTemplateColumns: 'minmax(0,2.3fr) minmax(0,1.5fr) minmax(0,2fr) minmax(0,1.35fr) minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,1.05fr)' }}
              >
                <span className="text-ink">Name</span>
                <span>Company</span>
                <span>Email</span>
                <span>Phone</span>
                <span>Lifecycle</span>
                <span>Tags</span>
                <span>Owner</span>
                <span>Updated</span>
              </div>
              {contacts.map((c) => (
                <ContactRow key={c.id} contact={c} />
              ))}
            </div>
          )}

          {total > 0 && <ListPagination page={page} pageSize={pageSize} total={total} noun="contacts" onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />}
        </div>
      </main>
    </>
  );
}

function ContactRow({ contact: c }: { contact: ContactResponse }) {
  return (
    <Link
      to="/contacts/$contactId"
      params={{ contactId: c.id }}
      role="row"
      className="grid h-13 shrink-0 items-center gap-2 border-b border-divider px-4 text-[13px] hover:bg-surface-subtle"
      style={{ gridTemplateColumns: 'minmax(0,2.3fr) minmax(0,1.5fr) minmax(0,2fr) minmax(0,1.35fr) minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,1.05fr)' }}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Avatar name={c.fullName} size="md" />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-ink">{c.fullName}</span>
          {c.jobTitle && <span className="truncate text-xs text-text-muted">{c.jobTitle}</span>}
        </span>
      </span>
      <span className="truncate text-text-secondary">{c.primaryCompany?.name ?? '—'}</span>
      <span className="truncate text-text-secondary">{c.email ?? '—'}</span>
      <span className="tabular-nums text-text-secondary">{c.phone ?? '—'}</span>
      <span>
        <Pill label={c.lifecycleStage === 'customer' ? 'Customer' : 'Lead'} color={c.lifecycleStage === 'customer' ? '#1E7A4C' : '#2E47D6'} />
      </span>
      <span className="flex flex-wrap gap-1">
        {c.tags.length === 0 && <span className="text-text-faint">—</span>}
        {c.tags.slice(0, 2).map((t) => (
          <span key={t.id} className="flex h-5.5 items-center rounded-[6px] border border-border px-2 text-xs font-medium text-text-secondary">
            {t.name}
          </span>
        ))}
      </span>
      <span className="flex min-w-0 items-center gap-2 text-text-secondary">
        {c.owner ? (
          <>
            <Avatar name={c.owner.displayName} size="sm" />
            <span className="truncate">{c.owner.displayName}</span>
          </>
        ) : (
          <span className="text-text-faint">Unassigned</span>
        )}
      </span>
      <span className="text-text-muted">{new Date(c.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
    </Link>
  );
}
